/**
 * Navigation UD af widgeten — også når den står i en fremmed iframe.
 *
 * Wix' «Embed HTML» (og lignende byggere) kører widget-koden i en iframe på et
 * andet domæne (`*.filesusr.com`). Et almindeligt link eller
 * `window.location.href = …` navigerer så KUN den lille iframe. Vores sider
 * sender `X-Frame-Options: SAMEORIGIN`, og Stripe Checkout nægter også at stå
 * i en frame, så kunden fik Chromes «… nægtede at oprette forbindelse» på
 * forløbs-, behandlings- og «Se hold»-knapperne og ved hver inline-betaling
 * (Lerloftet 10-10-2026). På en almindelig side (WordPress, script-tag direkte
 * i HTML) er widgeten IKKE indlejret, og der opfører alt sig som før.
 */

/** Står widgeten i en iframe (typisk Wix)? Cross-origin-adgang til `top` kaster → indlejret. */
export function erIndlejret(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return window.self !== window.top
  } catch {
    return true
  }
}

/**
 * `target`/`rel` til et link der fører væk fra widgeten. Indlejret åbnes det i
 * en ny fane — samme valg som widgetens købs-links allerede har (`_blank`
 * kræver kun `allow-popups`; `_top` er ikke tilladt i alle byggeres sandbox).
 * Ankre, `mailto:` og `tel:` bliver hvor de er.
 */
export function udLink(href: string | undefined): { target?: string; rel?: string } {
  if (!href || !/^https?:\/\//i.test(href) || !erIndlejret()) return {}
  return { target: '_blank', rel: 'noopener' }
}

/**
 * Send kunden videre (Stripe Checkout, vores egne sider) fra kode. Indlejret
 * prøves: ny fane → hele vinduet → iframen selv (det gamle, som sidste udvej).
 * Kaldes typisk lige efter et fetch; klikkets brugeraktivering gælder stadig,
 * så popup-blokkeren lader `window.open` passere.
 */
export function navigerUd(url: string): void {
  if (!erIndlejret()) {
    window.location.href = url
    return
  }
  const fane = window.open(url, '_blank')
  if (fane) {
    try { fane.opener = null } catch { /* ignoreres */ }
    return
  }
  try {
    window.top!.location.href = url
    return
  } catch {
    /* sandboxen tillader ikke top-navigation */
  }
  window.location.href = url
}

/**
 * Den side kunden står på, til brug som returmål. Indlejret er
 * `window.location` iframens egen adresse (`*.filesusr.com/...`), og en retur
 * dertil viser widgeten alene på en blank side. `document.referrer` er værtens
 * side (afhængigt af værtens referrer-politik mindst dens origin), som serveren
 * validerer mod nøglens tilladte domæner præcis som før.
 */
export function aktuelSide(): string {
  if (typeof window === 'undefined') return ''
  if (erIndlejret() && typeof document !== 'undefined' && /^https?:\/\//i.test(document.referrer)) {
    return document.referrer
  }
  return window.location.href
}
